
import React, { useState, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useDropzone } from 'react-dropzone';
import {
	Box,
	Button,
	Image,
	Input,
	Text,
	Heading,
	useToast,
} from '@chakra-ui/react';
import { saveAs } from 'file-saver';
import uploadIcon from '../../../assets/upload_icon.png';
import AutoTypeText from '../multistepform/autotype';
import { useSelector } from 'react-redux';
import DemoPaginatedData from './subLedgerPaginatedData';
import { Spinner } from "@chakra-ui/react";

const Demo = () => {
	const toast = useToast();
	const [data, setData] = useState([]);
	const [editing, setEditing] = useState(false);
	const companyId = useSelector((state) => state.companyId);
	const selectedLocation = useSelector((state) => state.selectedLocation);
	const selectedBranch = useSelector((state) => state.selectedBranch);
	const locationCode = useSelector((state) => state.locationCode);
	const [uploadMessageExcel, setUploadMessageExcel] = useState(undefined);
	const [errors, setErrors] = useState([]);
	const [hasValidationErrors, setHasValidationErrors] = useState(false);
	const [errorValue, setErrorValue] = useState(0);
	const [totalOpenBalance, setTotalOpenBalance] = useState({});
	const [bankList, setBankList] = useState({ glValues: [] });
	const [pagesWithErrors, setPagesWithErrors] = useState(new Set());
	const [currentPage, setCurrentPage] = useState(1);
	const [balanceErrors, setBalanceErrors] = useState([]);
	const itemsPerPage = 50;
	const [loading, setLoading] = useState(false);

	function toastCallRequestError(message = 'Request Failed') {
		return toast({
			title: message,
			status: 'error',
			duration: 3000,
			isClosable: true,
		});
	}

	function toastCallRequestWarning(message = 'Request Warning') {
		return toast({
			title: message,
			status: 'warning',
			duration: 3000,
			isClosable: true,
		});
	}

	function bigPrint(number) {
		return number ? number.toString() : '0';
	}

	function formatDateToDdMmYy(date) {
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = String(date.getFullYear()).slice(-2);
		return `${day}-${month}-${year}`;
	}
	const currentDate = new Date();
	const formattedDate = formatDateToDdMmYy(currentDate);

	useEffect(() => {
		const glData = async () => {
			try {
				setLoading(true);
				const response = await fetch(
					`${process.env.REACT_APP_API_URL}api-extra-subLedger-list.php?company_id=${companyId}&branch_id=${selectedBranch}&location_id=${selectedLocation}`,
					{
						method: 'GET',
					}
				);
				if (response.ok) {
					const data = await response.json();
					setBankList(data);
				} else {
					toastCallRequestError('Failed to fetch bank list data');
				}
			} catch {
				toastCallRequestError('Error fetching bank list data');
			} finally {
				setLoading(false);
			}
		};
		glData();
	}, [selectedBranch]);

	useEffect(() => {
		const newErrorValue = errors.reduce((acc, rowErrors) => {
			return (
				acc +
				Object.values(rowErrors).filter((error) => error !== null).length
			);
		}, 0);
		setErrorValue(newErrorValue);
	}, [errors]);

	// New useEffect to validate open balance whenever data changes
	useEffect(() => {
		if (data.length > 0) {
			validateOpenBalance(data);
		} else {
			setTotalOpenBalance({});
			setBalanceErrors([]);
		}
	}, [data, bankList]);

	const validateOpenBalance = (formattedData) => {
		const balanceByGlCode = formattedData.reduce((acc, row) => {
			const glCode = row.gl_code;
			const openBalance = parseFloat(row.open_balance) || 0;
			acc[glCode] = (acc[glCode] || 0) + openBalance;
			return acc;
		}, {});

		const newBalanceErrors = [];
		Object.keys(balanceByGlCode).forEach((glCode) => {
			const glValue = bankList.glValues.find(
				(item) => item.gl_code === glCode
			)?.value;
			const totalBalance = balanceByGlCode[glCode];
			if (glValue !== undefined && totalBalance > parseFloat(glValue)) {
				newBalanceErrors.push({
					gl_code: glCode,
					totalBalance,
					parentValue: parseFloat(glValue),
				});
			}
		});

		setTotalOpenBalance(balanceByGlCode);
		setBalanceErrors(newBalanceErrors);
	};

	const handleFileDrop = useCallback((acceptedFiles) => {
		const file = acceptedFiles[0];
		const reader = new FileReader();

		reader.onload = (event) => {
			const data = new Uint8Array(event.target.result);
			const workbook = XLSX.read(data, { type: 'array' });

			const sheetName = workbook.SheetNames[0];
			const worksheet = workbook.Sheets[sheetName];

			const excelData = XLSX.utils.sheet_to_json(worksheet, {
				header: 1,
			});

			const headers = excelData[0].map((header) => header.trim());
			const formattedData = excelData.slice(1).map((row) => {
				const rowData = {};
				row.forEach((cell, columnIndex) => {
					const header = headers[columnIndex];
					rowData[header] = cell;
				});
				return rowData;
			});

			const emptyRow = Object.fromEntries(
				headers.map((header) => [header, ''])
			);
			const dataWithEmptyKeys = formattedData.map((row) => ({
				...emptyRow,
				...row,
			}));

			const nonBlankRows = dataWithEmptyKeys.filter((row) =>
				Object.values(row).some(
					(cellValue) => cellValue !== null && cellValue !== ''
				)
			);

			setData(nonBlankRows);
			setUploadMessageExcel(undefined);
			// validateOpenBalance is now triggered by useEffect
		};

		reader.readAsArrayBuffer(file);
	}, [bankList]);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop: handleFileDrop,
		accept: '.xlsx',
	});

	const demoXlSX = bankList.message;
	function generateCSVData(demoXlSX) {
		if (!demoXlSX || demoXlSX.length === 0) {
			return null; // Return null to indicate no data
		}
		const headers = Object.keys(demoXlSX[0]);
		const headerRow = headers.join(',') + '\n';

		const rows = demoXlSX
			.map((item) => {
				const values = headers.map((header) => item[header] ?? '');
				return values.join(',');
			})
			.join('\n');

		return headerRow + rows;
	}

	const handleDownload = () => {
		if (!demoXlSX || demoXlSX.length === 0) {
			toastCallRequestWarning('The page is still loading. Please wait a few seconds and then try downloading again.');
			return;
		}

		try {
			// Extract only necessary fields
			const updatedData = demoXlSX.map((item) => ({
				gl_code: item.gl_code || '',
				sl_code: item.sl_code || '',
				sl_name: item.sl_name || '',
				open_balance: '' // keep it empty
			}));

			const csvData = generateCSVData(updatedData);
			if (!csvData) {
				toastCallRequestError('Failed to generate CSV data');
				return;
			}
			const blob = new Blob([csvData], {
				type: 'text/csv;charset=utf-8',
			});
			saveAs(
				blob,
				`Extra-SubLedger-List-${locationCode}-${formattedDate}.csv`
			);
		} catch {
			toastCallRequestError('Error generating download file');
		}
	};


	const handlePageChange = (newPage) => {
		setCurrentPage(newPage);
	};

	return (
		<Box
			width='100%'
			height='100%'
			display='flex'
			justifyContent='center'
			alignItems='center'
			flexDirection='column'
		>
			<Box w='100%' display='flex' justifyContent='space-around'>
				<Box
					width='38%'
					display='flex'
					justifyContent='space-around'
					alignItems='center'
				>
					<Box width='400px'>
						<Box
							border='2px dashed var(--chakra-colors-bgGraycolor)'
							p='15px'
							borderRadius='20px'
							opacity={editing ? '0.5' : '1'}
							pointerEvents={editing ? 'none' : 'painted'}
						>
							<Box
								{...getRootProps()}
								bg='var(--chakra-colors-mainBlueColor)'
								width='100%'
								height='100%'
								borderRadius='20px'
								display='flex'
								justifyContent='center'
								alignItems='center'
								color='white'
								padding='50px 0px'
								cursor='grab'
							>
								<Input {...getInputProps()} />
								{isDragActive ? (
									<Box
										display='flex'
										flexDirection='column'
										justifyContent='center'
										alignItems='center'
									>
										<Image
											src={uploadIcon}
											alt='upload icon'
											height='100px'
											width='100px'
										/>
										<Text>Drop the Excel file here...</Text>
									</Box>
								) : (
									<Box
										display='flex'
										flexDirection='column'
										justifyContent='center'
										alignItems='center'
									>
										<Image
											src={uploadIcon}
											alt='upload icon'
											height='100px'
											width='100px'
										/>
										<Text>Drag & Drop your files</Text>
									</Box>
								)}
							</Box>
						</Box>
						<Box
							display='flex'
							width='100%'
							justifyContent='center'
							gap='15px'
							paddingTop='45px'
						>
							<Button
								borderRadius='10px'
								border='1px solid #003060'
								p='20px 30px'
								width='50%'
								fontSize='15px'
								background='none'
								color='mainBlueColor'
								onClick={handleDownload}
								cursor='pointer'
							>
								Download Sample
							</Button>
						</Box>
					</Box>
				</Box>

				<Box
					width='58%'
					boxShadow='rgba(0, 0, 0, 0.24) 0px 3px 8px'
					borderRadius='15px'
					padding='15px'
				>
					<Heading
						mb='15px'
						color='var(--chakra-colors-mainBlueColor)'
						fontSize='28px'
					>
						Data Summary :
					</Heading>
					{loading && (
						<Box display="flex" justifyContent="center" mb="15px">
							<Spinner size="lg" color="blue.500" />
						</Box>
					)}
					<Text
						fontSize='1.7rem'
						fontWeight='600'
						marginBottom='10px'
						color='green'
					>
						Total lines : {data?.length || 0}
					</Text>
					<Text
						fontSize='1.7rem'
						fontWeight='600'
						marginBottom='10px'
						color='red'
					>
						Total Error : {errorValue}
					</Text>
					{balanceErrors.length > 0 && (
						<Text
							fontSize='1.7rem'
							fontWeight='600'
							marginBottom='10px'
							color='red'
						>
							Warning: Total balance exceeds parent value for GL Code(s):{' '}
							{balanceErrors
								.map(
									(err) =>
										`${err.gl_code} (Balance: ${bigPrint(
											err.totalBalance
										)}, Parent: ${bigPrint(err.parentValue)})`
								)
								.join(', ')}
						</Text>
					)}
					{/* <Text
						fontSize='1.7rem'
						fontWeight='600'
						color='mainBlueColor'
						marginBottom='5px'
					>
						Balance:{' '}
						{Object.keys(totalOpenBalance).length > 0
							? Object.entries(totalOpenBalance)
								.map(([glCode, balance]) => `${glCode}: ${bigPrint(balance)}`)
								.join(', ')
							: '0'}
					</Text> */}
					<Text
						fontSize='1.7rem'
						fontWeight='600'
						color='mainBlueColor'
						marginBottom='5px'
					>
						Balance:{' '}
						{Object.keys(totalOpenBalance).length > 0
							? bigPrint(
								Object.values(totalOpenBalance).reduce(
									(sum, balance) => sum + (parseFloat(balance) || 0),
									0
								)
							)
							: '0'}
					</Text>
					<Box mt='10px' mb='10px'>
						<Box
							fontSize='1.7rem'
							fontWeight='600'
							color='mainBlueColor'
						>
							Pages with errors:
						</Box>
						{Array.from(pagesWithErrors).join(', ')}
					</Box>
					<Box>
						<Text
							fontSize='1.7rem'
							fontWeight='600'
							color='mainBlueColor'
							marginBottom='5px'
						>
							Data Review Report -
						</Text>
						<AutoTypeText />
					</Box>
				</Box>
			</Box>
			<DemoPaginatedData
				data={data}
				setData={setData}
				currentPage={currentPage}
				itemsPerPage={itemsPerPage}
				editing={editing}
				setEditing={setEditing}
				onPageChange={handlePageChange}
				setTotalOpenBalance={setTotalOpenBalance}
				setErrorValue={setErrorValue}
				errors={errors}
				setErrors={setErrors}
				hasValidationErrors={hasValidationErrors}
				setHasValidationErrors={setHasValidationErrors}
				setPagesWithErrors={setPagesWithErrors}
				uploadMessageExcel={uploadMessageExcel}
				setUploadMessageExcel={setUploadMessageExcel}
				balanceErrors={balanceErrors}
			/>
		</Box>
	);
};

export default Demo;